import './sticky-columns.css';

/**
 * v-sticky-columns: fija a la izquierda las columnas marcadas con la clase `og-sticky-col`
 * de un v-data-table (Vuetify 1.5) mientras se desplaza horizontalmente.
 *
 * Uso:
 *   <v-data-table v-sticky-columns="'clave-de-la-tabla'" ...>
 *     <th class="og-sticky-col" data-sticky-key="name">…</th>   (cabecera)
 *     <td class="og-sticky-col">…</td>                           (filas)
 *
 * Las celdas fijas deben ser las primeras de cada fila y aparecer en el mismo orden en la
 * cabecera y en las filas. El desplazamiento `left` de cada una se calcula midiendo el ancho
 * real de las celdas fijas anteriores de la cabecera, así que funciona aunque las columnas
 * cambien de ancho o se oculten con el filtro de columnas. Se recalcula cuando cambia el
 * contenido de la tabla o su tamaño.
 *
 * Las columnas fijas se pueden ensanchar o estrechar arrastrando el borde derecho de su
 * cabecera (doble clic lo devuelve al ancho automático). El contenido que no cabe se corta
 * con puntos suspensivos. Con el valor de la directiva (una clave) los anchos se recuerdan en
 * el navegador; `data-sticky-key` identifica cada columna (si falta, se usa su posición).
 * Las cabeceras con la clase `og-sticky-col--no-resize` no se pueden redimensionar.
 */

export const STICKY_CLASS = 'og-sticky-col';
const LAST_STICKY_CLASS = 'og-sticky-col--last';
const SIZED_CLASS = 'og-sticky-col--sized';
const NO_RESIZE_CLASS = 'og-sticky-col--no-resize';
const RESIZER_CLASS = 'og-sticky-col__resizer';
const RESIZING_CLASS = 'og-sticky-columns--resizing';
const CONTAINER_CLASS = 'og-sticky-columns';
const STORAGE_PREFIX = 'og-sticky-columns:';
const MIN_WIDTH = 32;

function stickyCellsOf(row) {
  return Array.prototype.filter.call(row.children, cell => cell.classList.contains(STICKY_CLASS));
}

function columnKey(headerCell, index) {
  return headerCell.getAttribute('data-sticky-key') || String(index);
}

function loadWidths(storageKey) {
  if (!storageKey) {
    return {};
  }
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_PREFIX + storageKey)) || {};
  } catch (e) {
    return {};
  }
}

function saveWidths(storageKey, widths) {
  if (!storageKey) {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(widths));
  } catch (e) {
    // Sin almacenamiento (modo privado, cuota…): el ancho solo dura mientras se ve la tabla.
  }
}

function applyWidth(cell, width) {
  const value = width ? `${width}px` : '';
  cell.style.width = value;
  cell.style.minWidth = value;
  cell.style.maxWidth = value;
  cell.classList.toggle(SIZED_CLASS, !!width);
}

function startResize(el, headerCell, event) {
  const state = el.__ogStickyColumns;
  if (!state || event.button !== 0) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();

  const headerRow = headerCell.parentElement;
  const key = columnKey(headerCell, stickyCellsOf(headerRow).indexOf(headerCell));
  const startX = event.clientX;
  const startWidth = headerCell.getBoundingClientRect().width;
  document.body.classList.add(RESIZING_CLASS);

  const onMove = moveEvent => {
    state.widths[key] = Math.max(MIN_WIDTH, Math.round(startWidth + moveEvent.clientX - startX));
    scheduleLayout(el);
  };
  // Al soltar sobre la cabecera, el navegador dispara un clic que ordenaría la columna o
  // abriría la edición masiva: se descarta.
  const swallowClick = clickEvent => {
    clickEvent.stopPropagation();
    clickEvent.preventDefault();
  };
  const onUp = () => {
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
    document.body.classList.remove(RESIZING_CLASS);
    saveWidths(state.storageKey, state.widths);
    window.addEventListener('click', swallowClick, true);
    window.setTimeout(() => window.removeEventListener('click', swallowClick, true), 0);
  };
  document.addEventListener('pointermove', onMove);
  document.addEventListener('pointerup', onUp);
}

function resetWidth(el, headerCell, event) {
  const state = el.__ogStickyColumns;
  event.preventDefault();
  event.stopPropagation();
  const headerRow = headerCell.parentElement;
  delete state.widths[columnKey(headerCell, stickyCellsOf(headerRow).indexOf(headerCell))];
  saveWidths(state.storageKey, state.widths);
  scheduleLayout(el);
}

function ensureResizer(el, headerCell) {
  if (headerCell.classList.contains(NO_RESIZE_CLASS)) {
    return;
  }
  if (headerCell.querySelector(`.${RESIZER_CLASS}`)) {
    return;
  }
  const handle = document.createElement('span');
  handle.className = RESIZER_CLASS;
  handle.title = 'Arrastra para cambiar el ancho; doble clic para el ancho automático';
  handle.addEventListener('pointerdown', event => startResize(el, headerCell, event));
  handle.addEventListener('click', event => event.stopPropagation());
  handle.addEventListener('dblclick', event => resetWidth(el, headerCell, event));
  headerCell.appendChild(handle);
}

function layoutStickyColumns(el) {
  const table = el.querySelector('table');
  const state = el.__ogStickyColumns;
  if (!table || !state) {
    return;
  }

  const headerRow = table.querySelector('thead tr');
  const headerCells = headerRow ? stickyCellsOf(headerRow) : [];
  const widths = headerCells.map((cell, index) => state.widths[columnKey(cell, index)]);
  headerCells.forEach(cell => ensureResizer(el, cell));

  const rows = Array.prototype.map.call(table.querySelectorAll('tr'), stickyCellsOf);

  // Primero los anchos elegidos por el usuario, porque cambian lo que mide cada columna.
  rows.forEach(cells => {
    cells.forEach((cell, index) => applyWidth(cell, widths[index]));
  });

  const offsets = [];
  let left = 0;
  headerCells.forEach(cell => {
    offsets.push(left);
    left += cell.getBoundingClientRect().width;
  });

  rows.forEach(cells => {
    cells.forEach((cell, index) => {
      const offset = offsets[index] !== undefined ? offsets[index] : 0;
      cell.style.left = `${offset}px`;
      cell.classList.toggle(LAST_STICKY_CLASS, index === cells.length - 1);
    });
  });
}

function scheduleLayout(el) {
  const state = el.__ogStickyColumns;
  if (!state || state.frame) {
    return;
  }
  state.frame = window.requestAnimationFrame(() => {
    state.frame = null;
    layoutStickyColumns(el);
  });
}

export default {
  inserted(el, binding) {
    el.classList.add(CONTAINER_CLASS);

    const storageKey = typeof binding.value === 'string' ? binding.value : null;
    const state = {
      frame: null,
      mutationObserver: null,
      resizeObserver: null,
      onResize: null,
      storageKey,
      widths: loadWidths(storageKey),
    };
    el.__ogStickyColumns = state;

    // Filas nuevas, columnas ocultadas o texto cambiado: hay que volver a medir.
    // No se observan atributos para no reaccionar a los estilos que pone la propia directiva.
    state.mutationObserver = new MutationObserver(() => scheduleLayout(el));
    state.mutationObserver.observe(el, { childList: true, subtree: true, characterData: true });

    state.onResize = () => scheduleLayout(el);
    if (typeof window.ResizeObserver === 'function') {
      state.resizeObserver = new window.ResizeObserver(state.onResize);
      state.resizeObserver.observe(el);
      const table = el.querySelector('table');
      if (table) {
        state.resizeObserver.observe(table);
      }
    } else {
      window.addEventListener('resize', state.onResize);
    }

    scheduleLayout(el);
  },

  componentUpdated(el) {
    // Si la tabla tiene un :class dinámico (p. ej. vista compacta/extensa), Vue reescribe su
    // atributo class y se lleva la clase del contenedor: se vuelve a poner.
    el.classList.add(CONTAINER_CLASS);
    scheduleLayout(el);
  },

  unbind(el) {
    const state = el.__ogStickyColumns;
    if (!state) {
      return;
    }
    if (state.frame) {
      window.cancelAnimationFrame(state.frame);
    }
    if (state.mutationObserver) {
      state.mutationObserver.disconnect();
    }
    if (state.resizeObserver) {
      state.resizeObserver.disconnect();
    } else if (state.onResize) {
      window.removeEventListener('resize', state.onResize);
    }
    delete el.__ogStickyColumns;
  },
};
